import { useState, useCallback, useMemo } from "react";
import { Button, Dropdown, Flex, Tooltip } from "antd";
import { ExitIcon, MenuIcon, StarIcon, FilledStar } from "../../assets/icons";
import MuteIcon from "../../assets/icons/Mute";
import ApiService from "../../API/apiService";
import { API_ENDPOINTS } from "../../API/endpoints";
import { handleApiError, localStorageUtil } from "../../utils/CommonFunctions";
import ConfirmationModal from "./modals/ConfirmationModal";
import { useBridgeStore } from "../../store/apis/useBridgeStore";

const ChannelListItem = ({
  channel,
  getChannelListApi,
  getFavListApi,
  isFavoriteList = false,
  hideMoreOptions = false,
  showNotificationType = false,
  userSelfData,
  onListUpdate,
  onClick,
}) => {
  const [isConfirmationPopup, setIsConfirmationPopup] = useState(false);
  const [isFavorite, setIsFavorite] = useState(channel?.isFavorite || false);

  // Memoized values
  const channelId = channel?._id;
  const hasUnread = useMemo(
    () =>
      channel?.unreadCount > 0 ||
      channel?.unseenMessagesCount > 0 ||
      channel?.unreadThreadCount > 0,
    [channel?.unreadCount, channel?.unseenMessagesCount, channel?.unreadThreadCount]
  );
  // Thread replies are included so a chat with only unread replies still shows a badge
  const unreadCount =
    (channel?.unreadCount || channel?.unseenMessagesCount || 0) +
      (channel?.unreadThreadCount || 0) || "";
  const canMarkAsRead = channel?.unreadCount > 0 || channel?.unreadThreadCount > 0;
  const notificationType = showNotificationType
    ? channel?.notificationType
    : null;

  // Update user data in localStorage
  const updateUserData = useCallback(
    (newMuteChannels) => {
      let userDataFromStorage = localStorageUtil.getItem("userData");
      userDataFromStorage.mute_channels = newMuteChannels;
      localStorageUtil.setItem("userData", userDataFromStorage);
    },
    [userSelfData]
  );

  const triggerSidebarRefresh = useBridgeStore(
    (store) => store.triggerSidebarRefresh
  );
  const triggerThreadsRead = useBridgeStore((store) => store.triggerThreadsRead);

  // Refresh lists after operations
  const refreshLists = useCallback(async () => {
    await Promise.all([getChannelListApi(), getFavListApi()]);
  }, [getChannelListApi, getFavListApi]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(async () => {
    try {
      const endpoint = isFavorite
        ? `${API_ENDPOINTS.unChannelFavApi}/${channelId}`
        : `${API_ENDPOINTS.favouriteChannelApi}/${channelId}`;

      const response = await ApiService.put(endpoint);

      if (response?.data && response?.status === 1) {
        setIsFavorite(!isFavorite);
        await refreshLists();
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [channelId, isFavorite, refreshLists]);

  // Handle mute/unmute operations
  const handleMuteToggle = useCallback(async () => {
    try {
      onListUpdate(channel._id, { isMutedByMe: !channel.isMutedByMe });
      const endpoint = channel.isMutedByMe
        ? API_ENDPOINTS.unMuteChannel
        : API_ENDPOINTS.muteChannel;
      const payload = channel.isMutedByMe
        ? { channelIdToUnmute: channelId }
        : { channelIdToMute: channelId };

      const response = await ApiService.post(endpoint, payload);

      if (response?.status === 1) {
        updateUserData(response.data.mute_channels);
      }
    } catch (error) {
      onListUpdate(channel._id, { isMutedByMe: channel.isMutedByMe });
      handleApiError(error);
    }
  }, [channelId, updateUserData, channel.isMutedByMe]);

  // Handle read/unread toggle
  const handleReadStatusToggle = useCallback(async () => {
    try {
      const isUnread = canMarkAsRead;
      const endpoint = isUnread
        ? `${API_ENDPOINTS.seenChannelMessage}/${channelId}?all=true`
        : `${API_ENDPOINTS.unreadChannelMessageApi}/${channelId}`;
      const method = isUnread ? "get" : "put";

      const response = await ApiService[method](endpoint);

      if (response?.data) {
        let userDataFromStorage = localStorageUtil.getItem("userData");
        userDataFromStorage.lastActiveChat = { id: channelId, type: "channel" };
        localStorageUtil.setItem("userData", userDataFromStorage);

        if (isFavoriteList) {
          await getFavListApi();
        } else {
          await getChannelListApi();
        }
        // Marking as read also clears thread replies, so the Threads badge must re-fetch
        if (isUnread) {
          triggerSidebarRefresh(Date.now(), "threads");
          triggerThreadsRead(channelId);
        }
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [
    triggerSidebarRefresh,
    triggerThreadsRead,
    canMarkAsRead,
    channelId,
    userSelfData,
    isFavoriteList,
    getFavListApi,
    getChannelListApi,
  ]);

  // Handle confirmation success
  const handleConfirmationSuccess = useCallback(() => {
    refreshLists();
  }, [refreshLists]);

  // Handle confirmation modal actions
  const openConfirmationModal = useCallback(() => {
    setIsConfirmationPopup(true);
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setIsConfirmationPopup(false);
  }, []);

  // Memoized menu items
  const menuItems = useMemo(
    () => [
      {
        key: "read-status",
        label: canMarkAsRead ? "Mark as read" : "Mark as unread",
        icon: <MenuIcon />,
        onClick: handleReadStatusToggle,
      },
      {
        key: "favorite",
        label: isFavorite ? "Unfavorite" : "Favorite",
        icon: isFavorite ? <FilledStar /> : <StarIcon />,
        onClick: handleFavoriteToggle,
      },
      {
        key: "mute",
        label: channel.isMutedByMe ? "Unmute Channel" : "Mute Channel",
        icon: <MuteIcon />,
        onClick: handleMuteToggle,
      },
      ...(!channel?.isDefault
        ? [
          {
            key: "leave",
            label: "Leave Channel",
            className: "text-danger",
            icon: <ExitIcon />,
            onClick: openConfirmationModal,
          },
        ]
        : []),
    ],
    [
      canMarkAsRead,
      channel?.isDefault,
      isFavorite,
      channel.isMutedByMe,
      handleReadStatusToggle,
      handleFavoriteToggle,
      handleMuteToggle,
      openConfirmationModal,
    ]
  );

  function formatChannelName(channelName) {
    return channelName
    // return channelName.length < 20
    //   ? channelName
    //   : `${channelName.slice(0, unreadCount > 0 ? 14 : 18)}...`;
  }

  return (
    <div
      className="sidebar-channel-row"
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: onClick ? "pointer" : "default", width: "100%" }}
    >
      <Flex
        justify="space-between"
        align="center"
        gap="middle"
        className={`sidebar-channel-flex ${channel?.isMutedByMe ? "sidebar-mute" : ""
          }`}
      >
        <div className="channel-name-block sidebar-channel-left">
          <Tooltip
            arrow={{ pointAtCenter: true }}
            style={hasUnread ? { fontWeight: "bold", color: "#FFFFFF" } : {}}
            className={hasUnread ? "name highlight" : "name"}
            title={channel?.name || ""}
          >
            {channel?.name || "Channels"}
          </Tooltip>

          {notificationType && (
            <span
              className="notification-type-pill"
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginLeft: 8,
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: 11,
                lineHeight: 1.4,
                background: "rgba(255,255,255,0.08)",
                color: "#FFFFFF",
                letterSpacing: "0.02em",
              }}
            >
              {notificationType}
            </span>
          )}
        </div>

        {!hideMoreOptions && (
          <>
            {unreadCount && <span className="unread-msg-count">{unreadCount}</span>}

            <div
              className="mute-icon-wrapper"
              onClick={(e) => e.stopPropagation()}
            >
              {channel.isMutedByMe && <MuteIcon />}
              <Dropdown
                menu={{ items: menuItems }}
                className="ec-dropdown sidebar-channel-dropdown"
                trigger={["click"]}
                popupRender={(menu) => (
                  <div className="ec-dropdown__menu sidebar-channel-dropdown__menu">
                    {menu}
                  </div>
                )}
              >
                <Button
                  type="text"
                  className="options-menu-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fa-solid fa-ellipsis-vertical" />
                </Button>
              </Dropdown>
            </div>
          </>
        )}
      </Flex>

      {!hideMoreOptions && (
        <ConfirmationModal
          isOpen={isConfirmationPopup}
          onClose={closeConfirmationModal}
          channelId={channelId}
          onSuccess={handleConfirmationSuccess}
        />
      )}
    </div>
  );
};

export default ChannelListItem;
